import { useMemo, useState } from 'react';
import { TEMPLATES, type TemplateAudience } from '@emails/registry';
import { SAMPLE_DATA } from '@emails/sample-data';

const audienceLabels: Record<TemplateAudience, string> = {
  organizer: 'Booking · organizer',
  invitee: 'Invitee · teammate',
  vendor: 'Vendor · bar contact',
  account: 'Account · auth',
};

const audienceOrder: TemplateAudience[] = ['organizer', 'invitee', 'vendor', 'account'];

export default function EmailPreview() {
  const [selected, setSelected] = useState<string>(TEMPLATES[0].slug);
  const [viewport, setViewport] = useState<'desktop' | 'mobile'>('desktop');

  const grouped = useMemo(() => {
    const out: Record<TemplateAudience, typeof TEMPLATES> = {
      organizer: [],
      invitee: [],
      vendor: [],
      account: [],
    };
    for (const t of TEMPLATES) out[t.audience].push(t);
    return out;
  }, []);

  const tpl = TEMPLATES.find((t) => t.slug === selected)!;
  const rendered = useMemo(() => {
    try {
      return tpl.render(SAMPLE_DATA[tpl.slug] as never);
    } catch (e) {
      return { subject: '', preheader: '', html: `<pre style="padding:24px;color:#b03a3a;">${String(e)}</pre>` };
    }
  }, [tpl]);

  const triggerColor: Record<string, string> = {
    event: 'var(--success)',
    cron: 'var(--primary)',
    manual: 'var(--ink-soft)',
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 24, alignItems: 'start' }}>
      {/* ─── Left sidebar ──────────────────────────────────────────── */}
      <aside style={{ position: 'sticky', top: 96 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Email system</div>
        <h2 className="display" style={{ fontSize: 26, margin: '0 0 4px' }}>16 templates</h2>
        <p style={{ color: 'var(--ink-soft)', fontSize: 13, lineHeight: 1.55, marginBottom: 20 }}>
          Every transactional email Goaround sends, rendered with sample data. Same code path that production uses.
        </p>

        {audienceOrder.map((audience) => (
          <div key={audience} style={{ marginBottom: 20 }}>
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 10 }}>
              {audienceLabels[audience]}
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {grouped[audience].map((t) => {
                const isActive = t.slug === selected;
                return (
                  <li key={t.slug}>
                    <button
                      onClick={() => setSelected(t.slug)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: '10px 12px',
                        borderRadius: 10,
                        border: '1px solid transparent',
                        background: isActive ? 'var(--card)' : 'transparent',
                        borderColor: isActive ? 'var(--line)' : 'transparent',
                        cursor: 'pointer',
                        color: 'var(--ink)',
                      }}
                    >
                      <span className="mono" style={{ fontSize: 11, color: 'var(--ink-soft)', marginTop: 2 }}>{t.number}</span>
                      <span style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{t.title}</div>
                        <div style={{ fontSize: 11, color: 'var(--ink-soft)', marginTop: 2, lineHeight: 1.4 }}>{t.description}</div>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </aside>

      {/* ─── Right preview ─────────────────────────────────────────── */}
      <main>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
          <div style={{ minWidth: 0 }}>
            <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>
              {tpl.number} · {audienceLabels[tpl.audience]}
            </div>
            <h1 className="display" style={{ fontSize: 30, margin: '4px 0 6px' }}>{tpl.title}</h1>
            <div style={{ color: 'var(--ink-soft)', fontSize: 13 }}>{tpl.description}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button
              onClick={() => setViewport('desktop')}
              className="btn btn-sm"
              style={{
                background: viewport === 'desktop' ? 'var(--ink)' : 'transparent',
                color: viewport === 'desktop' ? 'var(--bg)' : 'var(--ink)',
                border: '1.5px solid var(--ink)',
              }}
            >Desktop</button>
            <button
              onClick={() => setViewport('mobile')}
              className="btn btn-sm"
              style={{
                background: viewport === 'mobile' ? 'var(--ink)' : 'transparent',
                color: viewport === 'mobile' ? 'var(--bg)' : 'var(--ink)',
                border: '1.5px solid var(--ink)',
              }}
            >Mobile</button>
          </div>
        </div>

        {/* Trigger metadata */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18 }}>
          <Tag label={tpl.trigger.toUpperCase()} color={triggerColor[tpl.trigger]} />
          <code style={{ background: 'var(--card)', borderRadius: 6, padding: '4px 10px', fontSize: 12, color: 'var(--ink)' }}>{tpl.triggerLabel}</code>
          <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
            From <strong style={{ color: 'var(--ink)' }}>{tpl.fromName}</strong> &lt;{tpl.fromAddress}&gt;
          </span>
        </div>

        {/* Inbox row preview */}
        <div style={{ border: '1px solid var(--line)', borderRadius: 12, background: 'var(--card)', padding: '16px 20px', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{tpl.fromName}</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--ink-soft)' }}>2:47 PM</span>
          </div>
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>{rendered.subject}</div>
          <div style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.4 }}>{rendered.preheader}</div>
        </div>

        {/* Iframe rendering of the actual HTML */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <iframe
            title={tpl.slug}
            srcDoc={rendered.html}
            style={{
              width: viewport === 'mobile' ? 390 : '100%',
              maxWidth: viewport === 'mobile' ? 390 : 720,
              height: 'min(85vh, 1100px)',
              border: '1px solid var(--line)',
              borderRadius: 14,
              background: '#f3eee5',
            }}
          />
        </div>

        {/* Raw payload */}
        <details style={{ marginTop: 16 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--ink-soft)', userSelect: 'none' }}>
            Sample payload
          </summary>
          <pre style={{ background: 'var(--card)', borderRadius: 10, padding: 14, marginTop: 8, fontSize: 12, lineHeight: 1.5, overflow: 'auto', maxHeight: 360 }}>
{JSON.stringify(SAMPLE_DATA[tpl.slug], null, 2)}
          </pre>
        </details>
      </main>
    </div>
  );
}

function Tag({ label, color }: { label: string; color: string }) {
  return (
    <span
      className="mono"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 10,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        background: 'var(--card)',
        border: '1px solid var(--line)',
        borderRadius: 999,
        padding: '4px 10px',
        color: 'var(--ink)',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, background: color }} />
      {label}
    </span>
  );
}
